/**
 * JobCard
 *
 * Displays a single job with status, type badge, and created time.
 * Uses EITELKEIT atoms for consistent styling.
 */

import React from 'react';
import type { CSSProperties } from 'react';
import { StatusDot, Badge } from '@ekelhaft-tools/eitelkeit/atoms';
import { Card } from '@ekelhaft-tools/eitelkeit/molecules';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';
import type { JobRecord, JobStatus } from '../../types.js';

export interface JobCardProps {
  job: JobRecord;
  onClick: (() => void) | null;
  style: CSSProperties | null;
}

/**
 * Map job status to StatusDot status
 */
function mapJobStatus(status: JobStatus): 'online' | 'waiting' | 'error' | 'offline' | 'busy' {
  switch (status) {
    case 'running':
      return 'busy';
    case 'pending':
      return 'waiting';
    case 'completed':
      return 'online';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'offline';
    default:
      return 'offline';
  }
}

/**
 * Map job status to Badge variant
 */
function mapStatusToBadgeVariant(
  status: JobStatus,
): 'info' | 'mint' | 'success' | 'danger' | 'warning' {
  switch (status) {
    case 'running':
      return 'info';
    case 'pending':
      return 'mint';
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'warning';
    default:
      return 'mint';
  }
}

/**
 * Map job type to Badge variant
 */
function mapTypeToBadgeVariant(type: string): 'sub' | 'info' | 'warning' {
  switch (type) {
    case 'pipeline':
      return 'sub';
    case 'deploy':
      return 'info';
    case 'command':
      return 'warning';
    default:
      return 'sub';
  }
}

/**
 * Format ISO date string to relative time
 */
function formatTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function JobCard({ job, onClick, style }: JobCardProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  const containerStyle: CSSProperties = {
    cursor: onClick ? 'pointer' : 'default',
    transition: tokens.transitions.normal,
    transform: isHovered && onClick ? 'translateX(4px)' : 'none',
    ...style,
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing[3],
  };

  const leftSideStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    minWidth: 0,
  };

  const jobNameStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.sm,
    color: tokens.colors.textPrimary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const metaStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
    marginTop: tokens.spacing[2],
  };

  const metaItemStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    color: tokens.colors.textMuted,
  };

  const badgeContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
  };

  const errorStyle: CSSProperties = {
    marginTop: tokens.spacing[2],
    padding: tokens.spacing[2],
    background: 'rgba(255, 71, 87, 0.1)',
    borderRadius: tokens.radii.sm,
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    color: tokens.colors.danger,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '60px',
    overflow: 'hidden',
  };

  const shouldPulse = job.status === 'running';

  return (
    <Card
      variant="default"
      padding="sm"
      hover={null}
      style={containerStyle}
      onClick={onClick ?? undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={headerStyle}>
        <div style={leftSideStyle}>
          <StatusDot
            status={mapJobStatus(job.status)}
            size="sm"
            pulse={shouldPulse}
            ringPulse={null}
            label={null}
          />
          <span style={jobNameStyle}>{job.name}</span>
        </div>
        <div style={badgeContainerStyle}>
          <Badge variant={mapTypeToBadgeVariant(job.payload.type)} size="sm">
            {job.payload.type.toUpperCase()}
          </Badge>
          <Badge variant={mapStatusToBadgeVariant(job.status)} size="sm">
            {job.status.toUpperCase()}
          </Badge>
        </div>
      </div>

      <div style={metaStyle}>
        <span style={metaItemStyle}>{job.id.slice(0, 8)}...</span>
        <span style={metaItemStyle}>{formatTime(job.createdAt)}</span>
        {job.progress > 0 && job.progress < 100 && (
          <span style={metaItemStyle}>{job.progress}%</span>
        )}
        {job.attemptsMade > 1 && (
          <span style={metaItemStyle}>
            attempt {job.attemptsMade}/{job.maxAttempts}
          </span>
        )}
      </div>

      {job.error && <div style={errorStyle}>{job.error}</div>}
    </Card>
  );
}
